import { Button, Card, Empty } from "antd";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ActionDto, NcDto } from "@audit-plan/shared";
import { ActionStatusPill, DueLabel, MonitoringOffPill, Pill, SeverityPill, fmtD } from "../pills";
import { HBar } from "../charts/Charts";
import { usePeople } from "../../api/people";
import { ResponsiveList } from "../ResponsiveList";
import { useIsMobile } from "../useIsMobile";

export function NcCapConsolidated({ ncs, actions, onNc, onAction }: { ncs: NcDto[]; actions: ActionDto[]; onNc: (id: string) => void; onAction: (id: string) => void }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const people = usePeople();
  const mobile = useIsMobile();
  if (!ncs.length) return <Card><Empty description={t("none")} /></Card>;
  // la NC sta sullo stabilimento (AUD-F8.4): con più stabilimenti le NC si raggruppano per stabilimento
  const groups = new Map<string, NcDto[]>();
  for (const n of ncs) (groups.get(n.factoryId) ?? groups.set(n.factoryId, []).get(n.factoryId)!).push(n);
  const many = groups.size > 1;
  return (
    <div style={{ display: "grid", gap: 16 }}>
      {[...groups.values()].map((g) => (
        <div key={g[0]!.factoryId} style={{ display: "grid", gap: 12 }}>
          {many && (
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap", fontWeight: 700 }}>
              <span>{g[0]!.factoryName}</span>
              {g[0]!.factoryCode && <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12, opacity: 0.6 }}>{g[0]!.factoryCode}</span>}
              <span style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>
                {g.length} NC · {g.filter((n) => n.status === "open").length} {t("open").toLowerCase()}
              </span>
              {g.some((n) => n.status === "open") && <MonitoringOffPill off={g[0]!.monitoringOff} />}
            </div>
          )}
      {g.map((n) => {
        const al = actions.filter((a) => a.ncId === n.id);
        const done = al.filter((a) => a.status === "done" || a.status === "closed").length;
        const cp = al.length ? Math.round((done / al.length) * 100) : 0;
        return (
          <Card
            key={n.id}
            size="small"
            title={
              mobile ? (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap", whiteSpace: "normal" }}>
                  <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{n.code}</span>
                  <SeverityPill s={n.severity} />
                  <Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>
                </span>
              ) : (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "var(--ap-mono)", fontSize: 12 }}>{n.code}</span>
                  <SeverityPill s={n.severity} />
                  <b>{n.title}</b>
                </span>
              )
            }
            extra={
              mobile ? null : (
                <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
                  <Pill tone={n.status === "closed" ? "ok" : "bad"}>{n.status === "closed" ? t("closed") : t("open")}</Pill>
                  <Button size="small" onClick={() => onNc(n.id)}>
                    {t("a_detail")}
                  </Button>
                </span>
              )
            }
          >
            {mobile && (
              <div style={{ fontWeight: 700, marginBottom: 6, overflowWrap: "anywhere" }}>{n.title}</div>
            )}
            <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 8 }}>
              {n.auditId ? <a onClick={() => nav(`/audits/${n.auditId}`)}>{n.auditCode}</a> : <span>{t("reg_badge")}</span>} · {n.factoryName} · {fmtD(n.createdAt)} · {t("owner")}: {people.name(n.ownerId)}
            </div>
            {mobile && (
              <Button block style={{ marginBottom: 10 }} onClick={() => onNc(n.id)}>
                {t("a_detail")}
              </Button>
            )}
            {al.length ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, maxWidth: 340 }}>
                  <HBar pct={cp} tone="ok" />
                  <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                    {cp}% · {al.length} {t("a_actionsLower")}
                  </span>
                </div>
                <ResponsiveList<ActionDto>
                  size="small"
                  rowKey="id"
                  pageSize={false}
                  dataSource={al}
                  onRowClick={(r) => onAction(r.id)}
                  card={{
                    code: (r) => r.code,
                    title: (r) => r.title,
                    tags: (r) => (
                      <>
                        <ActionStatusPill a={r} />
                        <DueLabel a={r} />
                      </>
                    ),
                    fields: (r) => [[t("progress"), `${r.progress || 0}%`]],
                  }}
                  columns={[
                    { title: t("code"), dataIndex: "code", render: (v: string) => <span style={{ fontFamily: "var(--ap-mono)" }}>{v}</span> },
                    { title: t("title"), dataIndex: "title" },
                    { title: t("dueDate"), render: (_: unknown, r: ActionDto) => <DueLabel a={r} /> },
                    { title: t("progress"), dataIndex: "progress", align: "right" as const, render: (v: number) => `${v || 0}%` },
                    { title: t("status"), render: (_: unknown, r: ActionDto) => <ActionStatusPill a={r} /> },
                  ]}
                />
              </>
            ) : (
              <div style={{ opacity: 0.6, fontSize: 13 }}>{t("a_noActions")}</div>
            )}
          </Card>
        );
      })}
        </div>
      ))}
    </div>
  );
}
